import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { InstanceService } from '../../core/instance/instance.service';

export type DashboardStyleScope = 'tasks' | 'dms';
export type AiRewriteModel = 'gpt-5-mini' | 'gpt-5' | 'gpt-5.6-luna' | 'gpt-5.6-terra';

export const DEFAULT_AI_REWRITE_PROMPT_PREFIX = 'Please rewrite the following email into a polished, professional format by removing the section titles and integrating the content into complete paragraphs. The email should flow naturally while addressing the points outlined by the titles. Ensure it maintains a clear, concise, and professional tone.';
export const DEFAULT_AI_REWRITE_SYSTEM_CONTENT = 'You are a professional email editor. Return only the rewritten email body, no subject, and keep track of who the email is addressed to and written by.';
export const DEFAULT_AI_REWRITE_TEMPERATURE = 0.5;

export interface DashboardStyleState {
  backgroundImageUrl: string | null;
  bgImageOpacityPct: number;
  cardBgColor: string;
  cardBgOpacityPct: number;
  taskcolBgColor: string;
  taskcolBgOpacityPct: number;
  taskHeaderColor: string;
  aiRewriteModel: AiRewriteModel;
  aiRewritePromptPrefix: string;
  aiRewriteSystemContent: string;
  aiRewriteTemperature: number;
}

type PersistedDashboardStyleSettings = Omit<DashboardStyleState, 'backgroundImageUrl'>;

interface DashboardIndexedDbConfig {
  dbName: string;
  storeName: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardStyleSettingsService {
  private readonly backgroundDbKey = 'backgroundImage';
  private readonly styleSettingsDbKey = 'styleSettings';

  private readonly defaultState: PersistedDashboardStyleSettings = {
    bgImageOpacityPct: 100,
    cardBgColor: '#ffffff',
    cardBgOpacityPct: 0,
    taskcolBgColor: '#d3d3d3',
    taskcolBgOpacityPct: 100,
    taskHeaderColor: '#495057',
    aiRewriteModel: 'gpt-5',
    aiRewritePromptPrefix: DEFAULT_AI_REWRITE_PROMPT_PREFIX,
    aiRewriteSystemContent: DEFAULT_AI_REWRITE_SYSTEM_CONTENT,
    aiRewriteTemperature: DEFAULT_AI_REWRITE_TEMPERATURE,
  };

  private readonly configByScope: Record<DashboardStyleScope, DashboardIndexedDbConfig> = {
    tasks: {
      dbName: 'reachme-ui-tasks-dashboard',
      storeName: 'tasksDashboard',
    },
    dms: {
      dbName: 'reachme-ui-dms-dashboard',
      storeName: 'dmsDashboard',
    },
  };

  private readonly stateByScope: Record<DashboardStyleScope, BehaviorSubject<DashboardStyleState>> = {
    tasks: new BehaviorSubject<DashboardStyleState>(this.createDefaultState()),
    dms: new BehaviorSubject<DashboardStyleState>(this.createDefaultState()),
  };

  private readonly dbPromiseByScope = new Map<DashboardStyleScope, Promise<IDBDatabase>>();
  private readonly loadPromiseByScope = new Map<DashboardStyleScope, Promise<void>>();
  private readonly loadedScopes = new Set<DashboardStyleScope>();
  private readonly currentObjectUrlByScope = new Map<DashboardStyleScope, string | null>();

  constructor(private readonly instanceService: InstanceService) {}

  watch(scope: DashboardStyleScope): Observable<DashboardStyleState> {
    void this.ensureLoaded(scope);
    return this.stateByScope[scope].asObservable();
  }

  getSnapshot(scope: DashboardStyleScope): DashboardStyleState {
    void this.ensureLoaded(scope);
    return this.stateByScope[scope].value;
  }

  async updateSettings(scope: DashboardStyleScope, patch: Partial<PersistedDashboardStyleSettings>): Promise<void> {
    await this.ensureLoaded(scope);

    const current = this.stateByScope[scope].value;
    const next: DashboardStyleState = {
      ...current,
      ...patch,
      backgroundImageUrl: current.backgroundImageUrl,
    };

    await this.saveStyleSettingsToIndexedDb(scope, this.toPersistedSettings(next));
    this.stateByScope[scope].next(next);
  }

  async setBackgroundFile(scope: DashboardStyleScope, blob: Blob): Promise<void> {
    await this.ensureLoaded(scope);
    await this.saveBackgroundToIndexedDb(scope, blob);

    const backgroundImageUrl = this.replaceObjectUrl(scope, URL.createObjectURL(blob));
    this.stateByScope[scope].next({
      ...this.stateByScope[scope].value,
      backgroundImageUrl,
    });
  }

  async removeBackgroundImage(scope: DashboardStyleScope): Promise<void> {
    await this.ensureLoaded(scope);
    await this.removeBackgroundFromIndexedDb(scope);

    this.replaceObjectUrl(scope, null);
    this.stateByScope[scope].next({
      ...this.stateByScope[scope].value,
      backgroundImageUrl: null,
    });
  }

  async reset(scope: DashboardStyleScope): Promise<void> {
    await this.ensureLoaded(scope);

    await Promise.all([
      this.removeBackgroundFromIndexedDb(scope),
      this.removeStyleSettingsFromIndexedDb(scope),
    ]);

    this.replaceObjectUrl(scope, null);
    this.stateByScope[scope].next(this.createDefaultState());
  }

  private createDefaultState(): DashboardStyleState {
    return {
      backgroundImageUrl: null,
      ...this.defaultState,
    };
  }

  private toPersistedSettings(state: DashboardStyleState): PersistedDashboardStyleSettings {
    return {
      bgImageOpacityPct: state.bgImageOpacityPct,
      cardBgColor: state.cardBgColor,
      cardBgOpacityPct: state.cardBgOpacityPct,
      taskcolBgColor: state.taskcolBgColor,
      taskcolBgOpacityPct: state.taskcolBgOpacityPct,
      taskHeaderColor: state.taskHeaderColor,
      aiRewriteModel: state.aiRewriteModel,
      aiRewritePromptPrefix: state.aiRewritePromptPrefix,
      aiRewriteSystemContent: state.aiRewriteSystemContent,
      aiRewriteTemperature: state.aiRewriteTemperature,
    };
  }

  private async ensureLoaded(scope: DashboardStyleScope): Promise<void> {
    if (this.loadedScopes.has(scope)) {
      return;
    }

    const existingLoad = this.loadPromiseByScope.get(scope);
    if (existingLoad) {
      return await existingLoad;
    }

    const loadPromise = this.loadScope(scope)
      .finally(() => this.loadPromiseByScope.delete(scope));
    this.loadPromiseByScope.set(scope, loadPromise);
    await loadPromise;
  }

  private async loadScope(scope: DashboardStyleScope): Promise<void> {
    try {
      const [blob, settings] = await Promise.all([
        this.loadBackgroundFromIndexedDb(scope),
        this.loadStyleSettingsFromIndexedDb(scope),
      ]);

      const nextState: DashboardStyleState = {
        ...this.createDefaultState(),
        ...(settings ?? {}),
        backgroundImageUrl: blob ? this.replaceObjectUrl(scope, URL.createObjectURL(blob)) : this.replaceObjectUrl(scope, null),
      };

      this.stateByScope[scope].next(nextState);
    } catch {
      this.stateByScope[scope].next(this.createDefaultState());
    } finally {
      this.loadedScopes.add(scope);
    }
  }

  private replaceObjectUrl(scope: DashboardStyleScope, nextUrl: string | null): string | null {
    const currentUrl = this.currentObjectUrlByScope.get(scope);
    if (currentUrl) {
      try {
        URL.revokeObjectURL(currentUrl);
      } catch {
        // ignore
      }
    }

    this.currentObjectUrlByScope.set(scope, nextUrl);
    return nextUrl;
  }

  private getIndexedDbConfig(scope: DashboardStyleScope): DashboardIndexedDbConfig {
    const baseConfig = this.configByScope[scope];
    const instanceKey = this.instanceService.getKey();
    return {
      dbName: `${baseConfig.dbName}-${instanceKey}`,
      storeName: `${baseConfig.storeName}-${instanceKey}`,
    };
  }

  private getDb(scope: DashboardStyleScope): Promise<IDBDatabase> {
    const existingPromise = this.dbPromiseByScope.get(scope);
    if (existingPromise) {
      return existingPromise;
    }

    const { dbName, storeName } = this.getIndexedDbConfig(scope);
    const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    this.dbPromiseByScope.set(scope, dbPromise);
    return dbPromise;
  }

  private async loadBackgroundFromIndexedDb(scope: DashboardStyleScope): Promise<Blob | null> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    return await new Promise<Blob | null>((resolve) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(this.backgroundDbKey);
      request.onsuccess = () => {
        const record = request.result as { id: string; blob?: Blob } | undefined;
        resolve(record?.blob ?? null);
      };
      request.onerror = () => resolve(null);
    });
  }

  private async saveBackgroundToIndexedDb(scope: DashboardStyleScope, blob: Blob): Promise<void> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put({ id: this.backgroundDbKey, blob, updatedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  private async removeBackgroundFromIndexedDb(scope: DashboardStyleScope): Promise<void> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    await new Promise<void>((resolve) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(this.backgroundDbKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  }

  private async loadStyleSettingsFromIndexedDb(scope: DashboardStyleScope): Promise<PersistedDashboardStyleSettings | null> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    return await new Promise<PersistedDashboardStyleSettings | null>((resolve) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(this.styleSettingsDbKey);
      request.onsuccess = () => {
        const record = request.result as { id: string; settings?: PersistedDashboardStyleSettings } | undefined;
        resolve(record?.settings ?? null);
      };
      request.onerror = () => resolve(null);
    });
  }

  private async saveStyleSettingsToIndexedDb(scope: DashboardStyleScope, settings: PersistedDashboardStyleSettings): Promise<void> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put({ id: this.styleSettingsDbKey, settings, updatedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  private async removeStyleSettingsFromIndexedDb(scope: DashboardStyleScope): Promise<void> {
    const db = await this.getDb(scope);
    const { storeName } = this.getIndexedDbConfig(scope);

    await new Promise<void>((resolve) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(this.styleSettingsDbKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  }
}